<script lang="ts" module>
  import { type PhilatelicItem } from '@briefmarken_katalog/types';
  import { getAllItems } from '$lib/services/philatelic_items';
  import { doLogin } from '$lib/stores/auth';
  import { listUpdateTriggered } from '$lib/stores/trigger_list_update';

  // Block / Booklet / Se-Tenants reference their stamps by id only, so resolving them
  // needs the whole catalog. Cache it across dialog openings and drop the cache
  // whenever the catalog changes.
  let catalogCache: Promise<PhilatelicItem[] | null> | null = null;

  listUpdateTriggered.subscribe(() => { catalogCache = null; });

  export function loadCatalog(): Promise<PhilatelicItem[] | null> {
    if (!catalogCache) catalogCache = getAllItems(doLogin);
    return catalogCache;
  }
</script>

<script lang="ts">
  import {
    type SingleStamp,
    type PhilatelicSet,
    type StampFromSet,
    type FaceValue,
    type ImageType,
    PhilatelicItemType,
  } from '@briefmarken_katalog/types';
  import { t } from '$lib/i18n';
  import Button from '$lib/components/ui/button/button.svelte';
  import CatalogDetailDialog from './CatalogDetailDialog.svelte';
  import type { CatalogDetailItem } from './catalogFacts';

  // Either the stamps are already embedded in the item (Set) or they are referenced
  // by id and have to be resolved against the catalog (Block / Booklet / Se-Tenants).
  // dateOfIssue is the issue date of the containing set — its stamps have none of their own.
  let { stampIds, stamps, dateOfIssue }: {
    stampIds?: string[];
    stamps?: StampFromSet[];
    dateOfIssue?: Date;
  } = $props();

  type Summary = {
    id: string;
    title: string;
    description: string;
    faceValue?: FaceValue;
    picture?: ImageType;
    parentTitle?: string;
    /** The stamp itself, handed to the detail dialog */
    item: CatalogDetailItem;
    /** Issue date to show for this stamp in the detail dialog */
    dateOfIssue?: Date;
  };

  let summaries = $state<Summary[]>([]);
  let loading = $state(true);
  let detailItem = $state<CatalogDetailItem | null>(null);
  let detailDate = $state<Date | undefined>(undefined);
  let isDetailOpen = $state(false);

  function openDetails(summary: Summary) {
    detailItem = summary.item;
    detailDate = summary.dateOfIssue;
    isDetailOpen = true;
  }

  $effect(() => {
    if (stamps) {
      summaries = stamps.map((stamp, i) => ({
        id: String(stamp._id ?? i),
        title: stamp.title,
        description: stamp.description,
        faceValue: stamp.face_value,
        picture: stamp.pictures?.[0],
        item: stamp,
        dateOfIssue,
      }));
      loading = false;
      return;
    }
    const wanted = stampIds ?? [];
    let cancelled = false;
    loading = true;
    loadCatalog()
      .then((items) => {
        if (cancelled) return;
        summaries = collect(items ?? [], wanted);
      })
      .finally(() => { if (!cancelled) loading = false; });
    return () => { cancelled = true; };
  });

  function collect(items: PhilatelicItem[], wanted: string[]): Summary[] {
    const byId = new Map<string, Summary>();
    for (const item of items) {
      if (item.type === PhilatelicItemType.SingleStamp) {
        const stamp = item as SingleStamp;
        byId.set(String(stamp._id), {
          id: String(stamp._id),
          title: stamp.title,
          description: stamp.description,
          faceValue: stamp.face_value,
          picture: stamp.pictures?.[0],
          item: stamp,
          dateOfIssue: stamp.date_of_issue,
        });
      } else if (item.type === PhilatelicItemType.Set) {
        const set = item as PhilatelicSet;
        for (const stamp of set.stamps ?? []) {
          byId.set(String(stamp._id), {
            id: String(stamp._id),
            title: stamp.title,
            description: stamp.description,
            faceValue: stamp.face_value,
            picture: stamp.pictures?.[0],
            parentTitle: set.title,
            item: stamp,
            dateOfIssue: set.date_of_issue,
          });
        }
      }
    }
    // Keep the order in which the collection references its stamps
    return wanted.map((id) => byId.get(id)).filter((s): s is Summary => !!s);
  }
</script>

{#if loading}
  <span class="text-sm text-muted-foreground">…</span>
{:else}
  <ul class="flex flex-col gap-1">
    {#each summaries as summary (summary.id)}
      <li class="flex items-center gap-2 rounded-md bg-secondary/60 px-2 py-1">
        {#if summary.picture}
          <img
            src={summary.picture.thumbnail ?? summary.picture.base64}
            alt={$t('alt.stamp')}
            class="h-10 w-auto max-w-14 object-contain shrink-0"
          />
        {/if}
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-medium">{summary.title}</div>
          {#if summary.parentTitle}
            <div class="truncate text-xs text-muted-foreground">{summary.parentTitle}</div>
          {/if}
        </div>
        {#if summary.faceValue}
          <span class="shrink-0 text-sm tabular-nums">{summary.faceValue.value} {summary.faceValue.unit}</span>
        {/if}
        <Button
          class="card-3d card-3d-anim shrink-0"
          size="sm"
          onclick={() => openDetails(summary)}
        >{$t('buttons.details')}</Button>
      </li>
    {/each}
  </ul>
{/if}

{#if isDetailOpen && detailItem}
<CatalogDetailDialog item={detailItem} dateOfIssue={detailDate} bind:isOpen={isDetailOpen} />
{/if}
